import { useEffect, useMemo, useState } from "react";
import { computeHatchReminders } from "@/lib/schedule-utils";
import { Bell, BellRing, AlarmClock, X, Clock } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const NOTIF_KEY = "aviary_last_notified_at";
const DISMISS_KEY = "aviary_reminders_dismissed_on";
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function RemindersBanner({ couples, groupName, incubationDays = 21 }) {
  const { t } = useI18n();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === todayStr()
  );
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  const reminders = useMemo(() => computeHatchReminders(couples, incubationDays), [couples, incubationDays]);
  const urgent = reminders.filter((r) => r.status === "overdue" || r.status === "today" || r.status === "soon");
  const upcoming = reminders.filter((r) => r.status === "upcoming").slice(0, 5);

  const requestPerm = async () => {
    if (typeof Notification === "undefined") return;
    const res = await Notification.requestPermission();
    setPermission(res);
  };

  // Throttled browser notification for urgent items: at most once per 6h
  useEffect(() => {
    if (permission !== "granted" || urgent.length === 0) return;
    const last = Number(localStorage.getItem(NOTIF_KEY) || 0);
    const now = Date.now();
    if (now - last < 6 * 3600 * 1000) return;
    try {
      const overdue = urgent.filter((r) => r.status === "overdue").length;
      const today = urgent.filter((r) => r.status === "today").length;
      const title = `Aviary Studio · ${groupName || "Hatch reminders"}`;
      const body =
        (overdue ? `${overdue} overdue · ` : "") +
        (today ? `${today} hatching today · ` : "") +
        `${urgent.length} egg${urgent.length > 1 ? "s" : ""} need attention`;
      new Notification(title, { body, tag: "aviary-hatch" });
      localStorage.setItem(NOTIF_KEY, String(now));
    } catch {/* background browser notification is best-effort */}
  }, [permission, urgent, groupName]);

  if (dismissed || reminders.length === 0) return null;

  if (urgent.length === 0 && upcoming.length === 0) return null;

  const labelFor = (r) => {
    if (r.status === "overdue") return `${Math.abs(r.days_remaining)}d ${t("reminders.overdue")}`;
    if (r.status === "today") return t("reminders.today");
    if (r.status === "soon") return `${t("reminders.in_days")} ${r.days_remaining}d`;
    return `${t("reminders.in_days")} ${r.days_remaining}d`;
  };
  const colorFor = (r) => {
    if (r.status === "overdue") return "bg-danger/10 text-danger border-danger/30";
    if (r.status === "today") return "bg-amber-100 text-amber-700 border-amber-200";
    if (r.status === "soon") return "bg-sand/20 text-espresso border-sand/40";
    return "bg-olive/10 text-olive border-olive/20";
  };

  return (
    <div
      className="bg-white rounded-xl border border-app-border p-4 shadow-sm"
      data-testid="reminders-banner"
      data-print-hide
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl ${urgent.length ? "bg-amber-100 text-amber-700" : "bg-olive/10 text-olive"} grid place-items-center flex-shrink-0`}>
          {urgent.length ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-heading text-lg font-semibold text-espresso">
                {t("reminders.title")} <span className="text-espresso/40 font-mono text-sm">({incubationDays} {t("reminders.days_unit")})</span>
              </div>
              <div className="text-xs text-espresso/60">
                {urgent.length > 0
                  ? `${urgent.length} ${urgent.length === 1 ? t("reminders.attention_one") : t("reminders.attention_other")}`
                  : `${upcoming.length} ${upcoming.length === 1 ? t("reminders.upcoming_one") : t("reminders.upcoming_other")}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {permission !== "granted" && typeof Notification !== "undefined" && (
                <button
                  onClick={requestPerm}
                  className="text-xs px-3 py-1.5 rounded-lg bg-olive/10 text-olive hover:bg-olive/15 font-semibold flex items-center gap-1.5"
                  data-testid="enable-notifications"
                >
                  <AlarmClock className="w-3.5 h-3.5" /> {t("reminders.enable")}
                </button>
              )}
              <button
                onClick={() => {
                  localStorage.setItem(DISMISS_KEY, todayStr());
                  setDismissed(true);
                }}
                className="p-1 text-espresso/40 hover:text-espresso/70"
                aria-label="Dismiss"
                data-testid="dismiss-reminders"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {urgent.slice(0, 8).map((r, i) => (
              <div
                key={`u-${i}`}
                className={`text-xs border rounded-full px-3 py-1.5 flex items-center gap-1.5 ${colorFor(r)}`}
                data-testid={`reminder-urgent-${i}`}
              >
                <Clock className="w-3 h-3" />
                <span className="font-semibold">#{r.couple_no}</span>
                <span className="opacity-70">·</span>
                <span>{t("couples.egg")} {r.egg_index + 1}</span>
                <span className="opacity-70">·</span>
                <span className="font-mono">{labelFor(r)}</span>
              </div>
            ))}
            {urgent.length === 0 && upcoming.map((r, i) => (
              <div
                key={`up-${i}`}
                className={`text-xs border rounded-full px-3 py-1.5 flex items-center gap-1.5 ${colorFor(r)}`}
                data-testid={`reminder-upcoming-${i}`}
              >
                <Clock className="w-3 h-3" />
                <span className="font-semibold">#{r.couple_no}</span>
                <span className="opacity-70">·</span>
                <span>{t("couples.egg")} {r.egg_index + 1}</span>
                <span className="opacity-70">·</span>
                <span className="font-mono">{labelFor(r)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
